// src/components/DonationComponent.js
/* global BigInt */
import GateRevealPanel from './GateRevealPanel';
import { getPanelRevealTitle } from '../utils/gatePresentation';
import React, {useState, useMemo, useRef} from 'react';
import { Link, useParams } from 'react-router-dom';
import {useWallet, useConnection} from '@solana/wallet-adapter-react';
import {PublicKey, SystemProgram, Transaction, TransactionInstruction} from '@solana/web3.js';
import { getMint, TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID } from '@solana/spl-token';
import ComponentControls from './ComponentControls';
import ComponentAnalyticsSummary from './ComponentAnalyticsSummary';
import {useGateAccess} from '../hooks/useGateAccess';
import { useDonationRecords } from '../hooks/useDonationRecords';
import { resolveTypography } from '../utils/resolveTypography';
import GateBadge from "./GateBadge";
import DirectPaymentUnlock from "./DirectPaymentUnlock";
import PasswordUnlock from "./PasswordUnlock";
import { resolveDonationToken } from '../utils/donationTokens';
import { buildPanelClassName } from '../utils/panelTheme';
import { useServiceFeePolicy } from '../hooks/useServiceFeePolicy';
import { decimalToAtomic, addSolServiceFee } from '../utils/serviceFees';
import { tokenPaymentInstructions } from '../utils/serviceFeeTransfers';
import ServiceFeeNotice from './ServiceFeeNotice';
import { usePaymentReceipt } from '../hooks/usePaymentReceipt';
import PaymentReceiptRecovery from './PaymentReceiptRecovery';
const PAYMENT_MEMO_PROGRAM = new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr');

export default function DonationComponent({
                                              comp,
                                              index,
                                              moveUp,
                                              moveDown,
                                              onEdit,
                                              isEditor = false,
                                              removeComponent,

                                              // gating passthrough
                                              gateType,
                                              selectedListId,
                                              onGateTypeChange,
                                              onGateListChange,
                                              extraGates,
                                              gateLogic,
                                              onExtraGatesChange,
                                              onGateLogicChange,
                                              donationCampaignOptions = [],
                                              donationGate,
                                              onDonationGateChange,
                                          onDirectPaymentGateChange,
                                          onDirectPaymentDraftChange,
                                              // style & media
                                              style = {},
                                              onStyleChange,
                                              coverUrl = '',
                                              onCoverChange,
                                              onPanelBackgroundImageChange,

                                              // NEW (optional): global typography
                                              pageTypography = null,
                                              ownerPublicKey = '',
                                          }) {
    const { username: routeUsername } = useParams();
    const ownerPublicKeyStr = ownerPublicKey ? String(ownerPublicKey) : '';
    const normalizeGate = (g) => {
        if (!g || g.type !== "directPayment") return g;
        return { ...g, payToUid: ownerPublicKeyStr || g.payToUid || "" };
    };
    const gate = normalizeGate(comp.gate || {type: 'public', listId: ''});
    const gates = useMemo(
        () => [
            gate,
            ...(Array.isArray(comp.extraGates) ? comp.extraGates.map(normalizeGate) : []),
        ],
        [gate, comp.extraGates, ownerPublicKeyStr]
    );
    const logic = comp.gateLogic === 'all' ? 'all' : 'any';

    const donationMap = useMemo(() => {
        const m = new Map();
        // expect options like { id, campaignName, currency }
        (donationCampaignOptions || []).forEach(o => {
            if (o?.id) {
                m.set(String(o.id), {
                    campaignName: o.campaignName || '',
                    currency: o.currency || 'SOL',
                    tokenMint: o.tokenMint || ''
                });
            }
        });
        return m;
    }, [donationCampaignOptions]);

    const [gateRefreshKey, setGateRefreshKey] = useState(0);
    const [hasAccess, isLoading, gateNamesLabel, gateNames, gateAccess] = useGateAccess(
        { gates, logic, donationLookup: donationMap },
        { spaceOwnerUid: ownerPublicKeyStr, isEditor: isEditor, refreshKey: gateRefreshKey, componentId: comp.id }
    );
    const { resetPasswordAccess, resetPasswordError } = gateAccess;
    const walletContext = useWallet();
    const {publicKey, sendTransaction, connected} = walletContext;
    const { policy: fees, loading: feesLoading, error: feesError } = useServiceFeePolicy();
    const walletAddress = useRef('');
    walletAddress.current = publicKey?.toBase58?.() || '';
    const {connection} = useConnection();
    const receipt = usePaymentReceipt({ wallet: walletContext, connection, kind: 'donation',
        spaceOwnerUid: ownerPublicKeyStr || comp.ownerUid || '', componentId: String(comp.id) });

    const [amount, setAmount] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const goalAmount = typeof comp.goalAmount === 'number' ? comp.goalAmount : null;
    const currency = comp.currency || 'SOL';
    const tokenConfig = resolveDonationToken(currency);
    const tokenMint = comp.tokenMint || tokenConfig.mint || '';

    const findSenderTokenAccount = async (mintPubkey) => {
        const programs = [TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID];
        for (const programId of programs) {
            const accounts = await connection.getParsedTokenAccountsByOwner(
                publicKey,
                { programId },
                "confirmed"
            );
            const filtered = accounts.value.filter(
                (acc) => acc.account?.data?.parsed?.info?.mint === mintPubkey.toBase58()
            );
            if (!filtered.length) continue;
            const best = filtered
                .map((acc) => ({
                    pubkey: acc.pubkey,
                    amount: Number(acc.account?.data?.parsed?.info?.tokenAmount?.uiAmount || 0),
                    programId,
                }))
                .sort((a, b) => b.amount - a.amount)[0];
            if (best?.pubkey) return best;
        }
        return null;
    };

    // Typography: panel overrides > global
    const typo = resolveTypography(pageTypography, style);
    const numToPx = v => (typeof v === 'number' ? `${v}px` : (v || undefined));
    const safeUsername = routeUsername || comp?.muultiName || '';
    const donationGateLinks = useMemo(() => {
        if (!safeUsername) return [];
        return (gates || [])
            .filter(g => g?.type === 'donation' && g?.donationCompId)
            .map(g => ({
                id: String(g.donationCompId),
                name: g.campaignName || 'Donation',
                currency: g.currency || '',
                href: `/${safeUsername}/d/${g.donationCompId}`
            }));
    }, [gates, safeUsername]);
    const detailHref =
        safeUsername && comp?.id
            ? `/${safeUsername}/d/${comp.id}`
            : null;

    // recipient/config (no early return)
    let recipient = null;
    let configError = '';
    if (!comp.recipientPublicKey) {
        configError = 'Donation panel not configured: no recipient address set.';
    } else {
        try {
            recipient = new PublicKey(comp.recipientPublicKey);
        } catch {
            configError = 'Donation panel not configured: invalid recipient address.';
        }
    }

    const donations = useDonationRecords({ spaceOwnerUid: ownerPublicKeyStr || comp.ownerUid || '',
        componentId: String(comp.id), currency, tokenMint, recipient: comp.recipientPublicKey || ownerPublicKeyStr,
        cluster: receipt.cluster });
    const totalDonated = donations.reduce((sum, donation) => sum + donation.amount, 0);
    const myDonations = useMemo(() => connected && publicKey
        ? donations.filter(donation => donation.sender === publicKey.toBase58()) : [], [donations, connected, publicKey]);
    const mySubtotal = myDonations.reduce((sum, donation) => sum + donation.amount, 0);

    const sigUrl = (sig) => (sig ? `https://solscan.io/tx/${sig}` : null);

    const handleDonate = async () => {
        if (configError) {
            setError(configError);
            return;
        }
        if (!connected || !publicKey) {
            alert('Please connect a Solana wallet to make a donation.');
            return;
        }

        if (feesLoading || feesError) { setError(feesError || 'Wait for service fees to load.'); return; }
        try {
            setLoading(true);
            const payerAddress = publicKey.toBase58();
            const ensureCurrent = () => { if (walletAddress.current !== payerAddress) throw new Error('Wallet changed. Please retry.'); };
            const amountValue = parseFloat(amount);
            if (isNaN(amountValue) || amountValue <= 0) throw new Error('Invalid donation amount.');

            if (tokenConfig.isNative || currency === 'SOL') {
                const amountRaw = decimalToAtomic(amount);
                const intent = await receipt.prepare({ amount, amountRaw, recipient: recipient.toBase58(), currency,
                    tokenMint: '', expectedVersion: fees?.version || 0 });
                const quote = intent.quote;
                const tx = new Transaction().add(SystemProgram.transfer({fromPubkey: publicKey, toPubkey: recipient, lamports: BigInt(amountRaw)}));
                addSolServiceFee(tx, publicKey, quote);
                tx.add(new TransactionInstruction({ programId: PAYMENT_MEMO_PROGRAM, keys: [], data: Buffer.from(intent.memo, 'utf8') }));
                const latest = await connection.getLatestBlockhash('confirmed');
                tx.recentBlockhash = latest.blockhash; tx.feePayer = publicKey;
                ensureCurrent();
                const sig = await sendTransaction(tx, connection);
                const submitted = receipt.remember(intent, sig);
                const confirmation = await connection.confirmTransaction({ signature: sig, ...latest }, 'confirmed');
                if (confirmation?.value?.err) throw new Error('Donation transaction failed.');
                await receipt.verify(submitted);
            } else {
                if (!tokenMint) throw new Error('Token mint is missing for this campaign.');
                const mintPubkey = new PublicKey(tokenMint);
                const senderAccount = await findSenderTokenAccount(mintPubkey);
                if (!senderAccount?.pubkey) throw new Error('Sender token account not found. Please ensure you have this token on this network.');
                const senderAta = senderAccount.pubkey;
                const tokenProgramId = senderAccount.programId || TOKEN_PROGRAM_ID;
                let mintInfo = tokenConfig.decimals != null ? { decimals: tokenConfig.decimals, tlvData: null } : await getMint(connection, mintPubkey, undefined, tokenProgramId);
                if (tokenProgramId === TOKEN_2022_PROGRAM_ID && !mintInfo?.tlvData) mintInfo = await getMint(connection, mintPubkey, undefined, tokenProgramId);
                const decimals = mintInfo.decimals ?? 0;
                const amountRaw = decimalToAtomic(amount, decimals);
                const intent = await receipt.prepare({ amount, amountRaw, recipient: recipient.toBase58(), currency,
                    tokenMint: tokenMint, expectedVersion: fees?.version || 0 });
                const quote = intent.quote;
                const instructions = await tokenPaymentInstructions({ connection, payer: publicKey, recipient, mint: mintPubkey, senderAta, tokenProgramId, mintInfo, decimals, quote });
                const tx = new Transaction().add(...instructions);
                addSolServiceFee(tx, publicKey, quote);
                tx.add(new TransactionInstruction({ programId: PAYMENT_MEMO_PROGRAM, keys: [], data: Buffer.from(intent.memo, 'utf8') }));
                const latest = await connection.getLatestBlockhash('confirmed');
                tx.recentBlockhash = latest.blockhash; tx.feePayer = publicKey;
                ensureCurrent();
                const sig = await sendTransaction(tx, connection);
                const submitted = receipt.remember(intent, sig);
                const confirmation = await connection.confirmTransaction({ signature: sig, ...latest }, 'confirmed');
                if (confirmation?.value?.err) throw new Error('Donation transaction failed.');
                await receipt.verify(submitted);
            }

            alert('Thank you for your donation!');
            setAmount('');
            setError('');
        } catch (err) {
            console.error(err);
            setError(err.message || 'Donation failed.');
        } finally {
            setLoading(false);
        }
    };

    const openDonationEditor = () => {
        const initial = {
            campaignName: comp.campaignName ?? '',
            description: comp.description ?? '',
            imageUrl: comp.imageUrl ?? '',
            goalAmount: typeof comp.goalAmount === 'number' ? comp.goalAmount : null,
            currency,
        };
        try {
            onEdit && onEdit(index, {type: 'donation', compId: comp.id, initial});
        } catch {
            onEdit && onEdit(index);
        }
    };

    const ProgressBar = ({value, max}) => {
        const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
        return (
            <div className="donation-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100}
                 aria-valuenow={Math.round(pct)}>
                <div className="donation-progress__fill" style={{width: `${pct}%`}}/>
            </div>
        );
    };

    const basePanelClass = buildPanelClassName({ style });
    const analyticsFooter = (
        <ComponentAnalyticsSummary
            comp={comp}
            ownerPublicKey={ownerPublicKeyStr}
            title="Component activity"
            compact
            footer
        />
    );

    if (isLoading) {
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
                <h3
                    style={{
                        fontFamily: typo.headers.fontFamily,
                        fontSize: numToPx(typo.headers.fontSize),
                        fontWeight: typo.headers.fontWeight,
                        color: typo.headers.color,
                        lineHeight: typo.headers.lineHeight
                    }}
                >
                    Checking access for {gateNamesLabel}…
                </h3>
                {analyticsFooter}
                <ComponentControls
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={openDonationEditor}
                    removeComponent={removeComponent}
                    gateType={gate.type}
                    selectedListId={gate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}

                    style={style}
                    onStyleChange={onStyleChange}
                    coverUrl={coverUrl}
                    onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                    extraGates={extraGates ?? comp.extraGates ?? []}
                    gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                    onExtraGatesChange={onExtraGatesChange}
                    onGateLogicChange={onGateLogicChange}
                />
            </div>
        </GateRevealPanel>
        );
    }

    if (!hasAccess) {
        const directPaymentGate =
            (gates || []).find(g => g?.type === "directPayment" && Number(g?.priceSol || 0) > 0);
        const passwordGate =
            (gates || []).find(g => g?.type === "password" && String(g?.password || '').length > 0);
        const deniedHeading = directPaymentGate ? 'x402 Error' : 'Access denied';
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
                <h1
                    style={{
                        fontFamily: typo.headers.fontFamily,
                        fontSize: numToPx(typo.headers.fontSize),
                        fontWeight: typo.headers.fontWeight,
                        color: typo.headers.color,
                        lineHeight: typo.headers.lineHeight
                    }}
                >
                    {deniedHeading} - {gateNamesLabel}
                </h1>
                {passwordGate ? (
                    <PasswordUnlock
                        gate={passwordGate}
                        componentId={comp.id}
                        onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                    />
                ) : null}
                {directPaymentGate ? (
                    <DirectPaymentUnlock
                        gate={directPaymentGate}
                        componentId={comp.id}
                        componentType={comp.type}
                        ownerPublicKey={ownerPublicKeyStr}
                        onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                    />
                ) : null}
                {donationGateLinks.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                        {donationGateLinks.map((d) => (
                            <div key={d.id} style={{ marginTop: 4 }}>
                                <Link to={d.href} className="detail-page-return-link">
                                    View {d.name}{d.currency ? ` (${d.currency})` : ''} details
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
                {analyticsFooter}
                <ComponentControls
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={openDonationEditor}
                    removeComponent={removeComponent}
                    gateType={gate.type}
                    selectedListId={gate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                    style={style}
                    onStyleChange={onStyleChange}
                    coverUrl={coverUrl}
                    onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                    extraGates={extraGates ?? comp.extraGates ?? []}
                    gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                    onExtraGatesChange={onExtraGatesChange}
                    onGateLogicChange={onGateLogicChange}
                />
            </div>
        </GateRevealPanel>
        );
    }

    return (
        <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
            {configError && (
                <p
                    className="error donation-error"
                    aria-live="polite"
                    style={{
                        fontFamily: typo.paragraph.fontFamily,
                        fontSize: numToPx(typo.paragraph.fontSize),
                        color: typo.paragraph.color,
                        lineHeight: typo.paragraph.lineHeight
                    }}
                >
                    {configError}
                </p>
            )}

            {comp.imageUrl && (
                <img
                    className="donation-image"
                    src={comp.imageUrl}
                    alt={comp.campaignName || 'Donation image'}
                />
            )}

            <h1
                style={{
                    fontFamily: typo.headers.fontFamily,
                    fontSize: numToPx(typo.headers.fontSize),
                    fontWeight: typo.headers.fontWeight,
                    color: typo.headers.color,
                    lineHeight: typo.headers.lineHeight
                }}
            >
                {comp.campaignName}
            </h1>

            {comp.description && (
                <p
                    style={{
                        fontFamily: typo.paragraph.fontFamily,
                        fontSize: numToPx(typo.paragraph.fontSize),
                        color: typo.paragraph.color,
                        lineHeight: typo.paragraph.lineHeight
                    }}
                >
                    {comp.description}
                </p>
            )}

            <p
                style={{
                    fontFamily: typo.paragraph.fontFamily,
                    fontSize: numToPx(typo.paragraph.fontSize),
                    color: typo.paragraph.color,
                    lineHeight: typo.paragraph.lineHeight
                }}
            >
                <strong>Raised:</strong> {totalDonated.toFixed(3)} {currency}
            </p>

            {typeof goalAmount === 'number' && goalAmount > 0 && (
                <>
                    <p
                        style={{
                            fontFamily: typo.paragraph.fontFamily,
                            fontSize: numToPx(typo.paragraph.fontSize),
                            color: typo.paragraph.color,
                            lineHeight: typo.paragraph.lineHeight
                        }}
                    >
                        <strong>Goal:</strong> {goalAmount} {currency}
                    </p>
                    <p
                        style={{
                            fontFamily: typo.paragraph.fontFamily,
                            fontSize: numToPx(typo.paragraph.fontSize),
                            color: typo.paragraph.color,
                            lineHeight: typo.paragraph.lineHeight
                        }}
                    >
                        <strong>Progress:</strong> {totalDonated.toFixed(3)} / {goalAmount} {currency}
                    </p>
                    <ProgressBar value={totalDonated} max={goalAmount}/>
                </>
            )}

            <label
                htmlFor={`donation-amount-${comp.id}`}
                style={{
                    fontFamily: typo.paragraph.fontFamily,
                    fontSize: numToPx(typo.paragraph.fontSize),
                    color: typo.paragraph.color,
                    lineHeight: typo.paragraph.lineHeight
                }}
            >
                Amount ({currency})
            </label>
            <input
                className="donation-input"
                id={`donation-amount-${comp.id}`}
                type="number"
                min="0"
                step="0.000001"
                placeholder={`Enter amount in ${currency}`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={loading || !!configError}
            />

            <ServiceFeeNotice kind="donation" currency={currency} amount={amount} decimals={tokenConfig.decimals}
                isNative={tokenConfig.isNative || currency === 'SOL'} />
            <div style={{marginTop: 8}}>
                <button
                    className="muulti-button"
                    onClick={handleDonate}
                    disabled={!amount || !!configError || loading || feesLoading || !!feesError}
                >
                    {loading ? 'Processing…' : 'Donate'}
                </button>
            </div>

            {error && error !== receipt.error && (
                <p
                    className="error donation-error"
                    aria-live="polite"
                    style={{
                        fontFamily: typo.paragraph.fontFamily,
                        fontSize: numToPx(typo.paragraph.fontSize),
                        color: typo.paragraph.color,
                        lineHeight: typo.paragraph.lineHeight
                    }}
                >
                    {error}
                </p>
            )}

            {/* ===== Your Donations (connected wallet) ===== */}
            {connected && publicKey && (
                <div className="my-donations" style={{marginTop: 16}}>
                    <h3
                        style={{
                            fontFamily: typo.headers.fontFamily,
                            fontSize: numToPx(typo.headers.fontSize),
                            fontWeight: typo.headers.fontWeight,
                            color: typo.headers.color,
                            lineHeight: typo.headers.lineHeight
                        }}
                    >
                        Your donations to this campaign
                    </h3>
                    {myDonations.length === 0 ? (
                        <p
                            style={{
                                fontFamily: typo.paragraph.fontFamily,
                                fontSize: numToPx(typo.paragraph.fontSize),
                                color: typo.paragraph.color,
                                lineHeight: typo.paragraph.lineHeight
                            }}
                        >
                            No donations from this wallet yet.
                        </p>
                    ) : (
                        <>
                            <p
                                style={{
                                    fontFamily: typo.paragraph.fontFamily,
                                    fontSize: numToPx(typo.paragraph.fontSize),
                                    color: typo.paragraph.color,
                                    lineHeight: typo.paragraph.lineHeight
                                }}
                            >
                                <strong>Subtotal:</strong> {mySubtotal.toFixed(3)} {currency}
                            </p>
                            <ul className="my-donations__list" style={{listStyle: 'none', padding: 0, marginTop: 8}}>
                                {myDonations.map((d) => {
                                    const ts = d.createdAt?.seconds ? new Date(d.createdAt.seconds * 1000) : null;
                                    const when = ts ? ts.toLocaleString() : 'pending…';
                                    const href = sigUrl(d.txSig);
                                    return (
                                        <li
                                            key={d.id}
                                            className="my-donations__item"
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'auto 1fr auto',
                                                gap: 8,
                                                alignItems: 'center',
                                                padding: '8px 0',
                                                borderTop: '1px solid rgba(0,0,0,0.08)'
                                            }}
                                        >
                                            <span style={{fontWeight: 600}}>{d.amount.toFixed(3)} {currency}</span>
                                            <span style={{opacity: 0.8}}>{when}</span>
                                            {href ? (
                                                <a
                                                    href={href}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="muulti-link"
                                                    style={{
                                                        color: typo.links.color,
                                                        textDecoration: typo.links.textDecoration || undefined
                                                    }}
                                                >
                                                    View Tx
                                                </a>
                                            ) : (
                                                <span style={{opacity: 0.6}}>No tx</span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>
            )}
            <PaymentReceiptRecovery receipt={receipt} onVerified={() => { setError(''); setAmount(''); }} />
            {detailHref && (
                <div style={{ marginTop: 8 }}>
                    <Link to={detailHref} target="_blank" rel="noreferrer" style={{
                            fontFamily: typo.links.fontFamily,
                            fontSize: typeof typo.links.fontSize === 'number' ? `${typo.links.fontSize}px` : typo.links.fontSize,
                            fontWeight: typo.links.fontWeight,
                            color: typo.links.color,
                            textDecoration: typo.links.textDecoration || 'underline',
                            lineHeight: typo.links.lineHeight || 'normal',
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer'
                        }}
                    >
                        Open in new page →
                    </Link>
                </div>
            )}
            {hasAccess && (
                <GateBadge gateNamesLabel={gateNamesLabel} onResetPasswordAccess={resetPasswordAccess} resetPasswordError={resetPasswordError} />
            )}
            {analyticsFooter}
            <ComponentControls
                compId={comp.id}
                index={index}
                moveUp={moveUp}
                moveDown={moveDown}
                onEdit={openDonationEditor}
                removeComponent={removeComponent}
                gateType={gate.type}
                selectedListId={gate.listId}
                onGateTypeChange={onGateTypeChange}
                onGateListChange={onGateListChange}
                donationCampaignOptions={donationCampaignOptions}
                donationGate={donationGate ?? comp.gate}
                onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                style={style}
                onStyleChange={onStyleChange}
                coverUrl={coverUrl}
                onCoverChange={onCoverChange}
                onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                extraGates={extraGates ?? comp.extraGates ?? []}
                gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                onExtraGatesChange={onExtraGatesChange}
                onGateLogicChange={onGateLogicChange}
            />
        </div>
        </GateRevealPanel>
    );
}




